import { makeAuthenticatedRequest } from "./marketOverviewApi.js";
import { getApiUrl } from "../utils/envConfig";
import { getAuthToken } from "../utils/authUtils";
import { getApiSecurityHeaders } from "../config/securityHeaders";
import type { components } from "./generated/schema";

// Request/response contracts sourced from the FastAPI OpenAPI schema via
// openapi-typescript (npm run gen:api-types). These types are generated
// from the backend Pydantic models, so they can't drift from what the API
// actually accepts and returns — CI fails the build if schema.ts is stale.
type PhysicalLocationResponse =
  components["schemas"]["PhysicalLocationResponse"];
type PhysicalFranchiseTrendAnalysisResponse =
  components["schemas"]["PhysicalFranchiseTrendAnalysisResponse"];
type PhysicalFranchiseOverviewResponse =
  components["schemas"]["PhysicalFranchiseOverviewResponse"];
type FranchiseNamesResponse = components["schemas"]["FranchiseNamesResponse"];
type PhysicalFranchiseDrillDownResponse =
  components["schemas"]["PhysicalFranchiseDrillDownResponse"];

// Result of a CSV export helper: it streams a blob and triggers a browser
// download as a side effect, returning only the outcome (not a JSON body).
type ExportResult = { success: boolean; filename: string };

// A failed-export error that carries the HTTP status and the API's human-readable
// `detail` (e.g. "No data matches the selected filters."), so the caller can show
// the backend's message instead of a bare status code.
type ExportError = Error & { status?: number; detail?: string };

// Turn a non-OK export response into an Error, preferring the API's JSON `detail`
// so the UI can surface a human message. Carries `status`/`detail` so the caller
// can tell an expected empty result (404 with detail) from an unexpected failure.
const buildFranchiseExportError = async (
  response: Response
): Promise<ExportError> => {
  let detail: string | undefined;
  try {
    const body = await response.json();
    if (body && typeof body.detail === "string") detail = body.detail;
  } catch {
    // Non-JSON error body — fall back to the status code below.
  }
  const error: ExportError = new Error(
    detail ?? `HTTP error! status: ${response.status}`
  );
  error.status = response.status;
  error.detail = detail;
  return error;
};

// Loose shape for the page-level/tab-specific filter object the franchise
// callers pass in. The endpoints restructure it into the generated request
// bodies (PhysicalFranchise*Request) before sending, so this stays permissive.
type FranchiseFilterInput = Record<string, any>;

export const fetchPhysicalCardsData = async ({
  id_client,
  country,
  filters,
  searchTerm,
}): Promise<Record<string, unknown>> => {
  try {
    let searchParam = "";
    if (searchTerm && searchTerm.trim() !== "") {
      searchParam = `?search=${encodeURIComponent(searchTerm.trim())}`;
    }

    const url = `${getApiUrl()}/client/${id_client}/country/${country}/score_cards${searchParam}`;

    const requestOptions: Record<string, any> = {
      method: "POST",
    };

    // filters is already `{ filter: {...} }` from convertFiltersToApiFormat,
    // which matches the ScoreCardFilterRequest shape. Don't re-wrap.
    if (filters) {
      requestOptions.body = JSON.stringify(filters);
    }

    const cardsData = await makeAuthenticatedRequest(url, requestOptions);
    return cardsData;
  } catch (error) {
    throw new Error("Failing fetching physical cards data: " + error.message);
  }
};

export const fetchPhysicalTableData = async ({
  id_client,
  country,
  page,
  limit,
  filters,
  sorter,
  searchTerm,
}): Promise<PhysicalLocationResponse> => {
  try {
    let searchParam = "";
    if (searchTerm && searchTerm.trim() !== "") {
      searchParam = `&search=${encodeURIComponent(searchTerm.trim())}`;
    }

    const url = `${getApiUrl()}/client/${id_client}/country/${country}/data_table?page=${page}&limit=${limit}${searchParam}`;

    const requestOptions: Record<string, any> = {
      method: "POST",
    };

    // filters is already `{ filter: {...} }` from convertFiltersToApiFormat.
    // data_table embeds multiple Body params, so the top-level shape must be
    // `{ filter, sort }`. Spread the pre-wrapped filter, then add sort.
    const body = filters ? { ...filters } : {};
    if (sorter && Object.keys(sorter).length > 0) {
      body.sort = sorter;
    }
    // Only include body if there's something to send
    if (Object.keys(body).length > 0) {
      requestOptions.body = JSON.stringify(body);
    }

    const tableData = await makeAuthenticatedRequest(url, requestOptions);
    return tableData;
  } catch (error) {
    throw new Error("Failing fetching physical table data: " + error.message);
  }
};

export const fetchPhysicalFilterDimensions = async ({
  id_client,
  country,
}: {
  id_client: string;
  country: string;
}): Promise<Record<string, unknown>> => {
  try {
    const url = `${getApiUrl()}/client/${id_client}/country/${country}/filter_dimensions`;
    const filterData = await makeAuthenticatedRequest(url);
    return filterData;
  } catch (error) {
    throw new Error(
      "Failing fetching physical filter dimensions: " + error.message
    );
  }
};

export const fetchPhysicalFilterDimension = async ({
  id_client,
  country,
  dimension,
  page = 1,
  limit = 50,
  search,
  signal,
}): Promise<Record<string, unknown>> => {
  try {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (search) params.set("search", search);
    const url = `${getApiUrl()}/client/${id_client}/country/${country}/filter-dimensions/${dimension}?${params.toString()}`;
    return makeAuthenticatedRequest(url, { signal });
  } catch (error) {
    throw new Error(
      "Failed fetching physical filter dimension: " + error.message
    );
  }
};

export const exportPhysicalTableData = async ({
  id_client,
  country,
  filters,
  sorter,
  searchTerm,
  totalRows = 0,
  onProgress,
}): Promise<ExportResult> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 300000); // 5 min

  try {
    let searchParam = "";
    if (searchTerm && searchTerm.trim() !== "") {
      searchParam = `?search=${encodeURIComponent(searchTerm.trim())}`;
    }

    const url = `${getApiUrl()}/client/${id_client}/country/${country}/data_table/export${searchParam}`;

    // Get token for authentication
    const token = await getAuthToken();

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...getApiSecurityHeaders(), // Add security headers
    };

    // filters is already `{ filter: {...} }` from convertFiltersToApiFormat.
    // data_table/export embeds multiple Body params, so the top-level shape must be
    // `{ filter, sort }`. Spread the pre-wrapped filter, then add sort.
    const body = filters ? { ...filters } : {};
    if (sorter && Object.keys(sorter).length > 0) {
      body.sort = sorter;
    }

    const response = await fetch(url, {
      method: "POST",
      headers: headers,
      body: Object.keys(body).length > 0 ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // Use totalRows from table data (already fetched), fall back to header
    const expectedRows =
      totalRows || parseInt(response.headers.get("X-Exported-Rows") || "0");

    // Stream the response, tracking progress by counting rows (newlines)
    const reader = response.body.getReader();
    const chunks = [];
    let rowsReceived = 0;

    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;

      chunks.push(value);

      if (onProgress && expectedRows > 0) {
        for (let i = 0; i < value.length; i++) {
          if (value[i] === 10) rowsReceived++; // 10 = '\n'
        }
        const pct = Math.min(
          Math.floor((rowsReceived / expectedRows) * 100),
          99
        );
        onProgress(pct);
      }
    }

    const blob = new Blob(chunks, { type: "text/csv" });

    // Get filename from Content-Disposition header if available
    const contentDisposition = response.headers.get("Content-Disposition");
    let filename = `Physical_Location_export_${country}_${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(
        /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
      );
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, "");
      }
    }

    // Create blob URL and trigger download
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up the blob URL
    window.URL.revokeObjectURL(blobUrl);

    return { success: true, filename };
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === "AbortError") {
      throw new Error(
        "Export timed out. The dataset may be too large — try applying filters to reduce the export size."
      );
    }
    throw new Error("Failing to export physical table data: " + error.message);
  }
};

/**
 * Fetch filter dimensions for Physical Franchise
 * Uses the /filter-dimension endpoint
 */
export const fetchPhysicalFranchiseFilterDimensions = async ({
  id_client,
  country,
  signal,
}): Promise<Record<string, unknown>> => {
  try {
    // The API expects country as a list parameter, so we need to pass it as an array
    const url = `${getApiUrl()}/filter-dimensions?id_client=${id_client}&country=${country}`;
    const filterData = await makeAuthenticatedRequest(url, {
      method: "GET",
      timeout: 120000, // 2 minutes timeout for slow filter-dimensions queries
      signal, // Add cancellation signal
    });
    return filterData;
  } catch (error) {
    throw new Error(
      "Failing fetching physical franchise filter dimensions: " + error.message
    );
  }
};

/**
 * Fetch trend analysis data for Physical Franchise.
 * First-class page-level filters (geo_level_1, geo_level_2, platform_presence) go in query params;
 * tab-specific (franchise_names) go in body.
 */
export const fetchPhysicalFranchiseTrendAnalysis = async ({
  id_client,
  country,
  refresh_from,
  refresh_to,
  filter = {},
  signal
}: {
  id_client: string;
  country: string;
  refresh_from?: string;
  refresh_to?: string;
  filter?: FranchiseFilterInput;
  signal?: AbortSignal;
}): Promise<PhysicalFranchiseTrendAnalysisResponse> => {
  try {
    const queryParams: string[] = [];
    if (refresh_from) queryParams.push(`refresh_from=${refresh_from}`);
    if (refresh_to) queryParams.push(`refresh_to=${refresh_to}`);
    // First-class page-level filters (same as geo_level_1/2 – affect chart and table)
    if (filter.geo_level_1) queryParams.push(`geo_level_1=${encodeURIComponent(filter.geo_level_1)}`);
    if (filter.geo_level_2) queryParams.push(`geo_level_2=${encodeURIComponent(filter.geo_level_2)}`);
    if (filter.platform_presence?.length) {
      filter.platform_presence.forEach((p) => queryParams.push(`platform_presence=${encodeURIComponent(p)}`));
    }
    const queryString = queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
    const url = `${getApiUrl()}/client/${id_client}/country/${country}/franchise/trend_analysis${queryString}`;

    const requestOptions: Record<string, any> = { method: "POST", signal, timeout: 120000 };
    const cleanedFilter: Record<string, unknown> = {};
    if (filter.franchise_names?.length) cleanedFilter.franchise_names = filter.franchise_names;
    if (Object.keys(cleanedFilter).length > 0) {
      requestOptions.body = JSON.stringify({ filter: cleanedFilter });
    }

    return await makeAuthenticatedRequest(url, requestOptions);
  } catch (error) {
    throw new Error("Failing fetching physical franchise trend analysis: " + error.message);
  }
};

/**
 * Fetch overview data for Physical Franchise.
 * First-class page-level filters (geo_level_1, geo_level_2, platform_presence) go in query params;
 * tab-specific (metric_filters, platform_filters) and sort go in body.
 */
export const fetchPhysicalFranchiseOverview = async ({
  id_client,
  country,
  refresh_from,
  refresh_to,
  page = 1,
  limit = 20,
  filter = {},
  sort = []
}: {
  id_client: string;
  country: string;
  refresh_from?: string;
  refresh_to?: string;
  page?: number;
  limit?: number;
  filter?: FranchiseFilterInput;
  sort?: unknown[];
}): Promise<PhysicalFranchiseOverviewResponse> => {
  try {
    const queryParams = [
      `refresh_from=${refresh_from}`,
      `refresh_to=${refresh_to}`,
      `page=${page}`,
      `limit=${limit}`,
    ];
    // First-class page-level filters (same as geo_level_1/2 – affect chart and table)
    if (filter.geo_level_1) queryParams.push(`geo_level_1=${encodeURIComponent(filter.geo_level_1)}`);
    if (filter.geo_level_2) queryParams.push(`geo_level_2=${encodeURIComponent(filter.geo_level_2)}`);
    if (filter.platform_presence?.length) {
      filter.platform_presence.forEach((p) => queryParams.push(`platform_presence=${encodeURIComponent(p)}`));
    }
    const queryString = `?${queryParams.join('&')}`;
    const url = `${getApiUrl()}/client/${id_client}/country/${country}/franchise/overview${queryString}`;

    const requestOptions: Record<string, any> = { method: "POST" };
    const body: Record<string, unknown> = {};
    const tabFilter: Record<string, unknown> = {};
    if (filter.metric_filters && Object.keys(filter.metric_filters).length > 0) {
      tabFilter.metric_filters = filter.metric_filters;
    }
    if (filter.platform_filters && Object.keys(filter.platform_filters).length > 0) {
      tabFilter.platform_filters = filter.platform_filters;
    }
    if (Object.keys(tabFilter).length > 0) body.filter = tabFilter;
    if (sort?.length > 0) body.sort = sort;
    if (Object.keys(body).length > 0) requestOptions.body = JSON.stringify(body);

    return await makeAuthenticatedRequest(url, requestOptions);
  } catch (error) {
    throw new Error("Failed to fetch physical franchise overview: " + error.message);
  }
};

/**
 * Fetch paginated distinct franchise names for the drill-down modal selector.
 */
export const fetchPhysicalFranchiseNames = async ({
  id_client,
  country,
  refresh_from,
  refresh_to,
  page = 1,
  limit = 50,
  search = "",
}): Promise<FranchiseNamesResponse> => {
  try {
    const params = new URLSearchParams({
      refresh_from,
      refresh_to,
      page: String(page),
      limit: String(limit),
    });
    if (search && search.trim()) params.set("search", search.trim());
    const url = `${getApiUrl()}/client/${id_client}/country/${country}/franchise/names?${params}`;
    return await makeAuthenticatedRequest(url, { method: "GET" });
  } catch (error) {
    throw new Error("Failed to fetch franchise names: " + error.message);
  }
};

/**
 * Fetch drill-down outlet data for Physical Franchise.
 * First-class page-level filters (geo_level_1, geo_level_2, platform_presence) in query params;
 * tab-specific (metric_filters) and sort in body.
 */
export const fetchPhysicalFranchiseDrillDown = async ({
  id_client,
  country,
  franchise_names,
  refresh_from,
  refresh_to,
  page = 1,
  limit = 20,
  filter = {},
  sort = [],
}: {
  id_client: string;
  country: string;
  franchise_names: string[];
  refresh_from?: string;
  refresh_to?: string;
  page?: number;
  limit?: number;
  filter?: FranchiseFilterInput;
  sort?: unknown[];
}): Promise<PhysicalFranchiseDrillDownResponse> => {
  try {
    const queryParams = [
      `refresh_from=${refresh_from}`,
      `refresh_to=${refresh_to}`,
      `page=${page}`,
      `limit=${limit}`,
    ];
    franchise_names.forEach((name) => {
      queryParams.push(`franchise_names=${encodeURIComponent(name)}`);
    });
    if (filter.geo_level_1) queryParams.push(`geo_level_1=${encodeURIComponent(filter.geo_level_1)}`);
    if (filter.geo_level_2) queryParams.push(`geo_level_2=${encodeURIComponent(filter.geo_level_2)}`);
    if (filter.platform_presence?.length) {
      filter.platform_presence.forEach((p) => queryParams.push(`platform_presence=${encodeURIComponent(p)}`));
    }
    const queryString = `?${queryParams.join('&')}`;
    const url = `${getApiUrl()}/client/${id_client}/country/${country}/franchise/outlets${queryString}`;

    const requestOptions: Record<string, any> = { method: "POST" };
    const body: Record<string, unknown> = {};
    if (filter.metric_filters && Object.keys(filter.metric_filters).length > 0) {
      body.filter = { metric_filters: filter.metric_filters };
    }
    if (sort?.length > 0) body.sort = sort;
    if (Object.keys(body).length > 0) requestOptions.body = JSON.stringify(body);

    return await makeAuthenticatedRequest(url, requestOptions);
  } catch (error) {
    throw new Error("Failed to fetch physical franchise drill-down: " + error.message);
  }
};

/**
 * Export franchise overview data to CSV
 * First-class page-level filters (geo_level_1, geo_level_2, platform_presence) go in query params;
 * tab-specific (metric_filters, platform_filters) and sort go in body.
 */
export const exportPhysicalFranchiseOverview = async ({
  id_client,
  country,
  refresh_from,
  refresh_to,
  filter = {},
  sort = []
}: {
  id_client: string;
  country: string;
  refresh_from?: string;
  refresh_to?: string;
  filter?: FranchiseFilterInput;
  sort?: unknown[];
}): Promise<ExportResult> => {
  try {
    const queryParams = [
      `refresh_from=${refresh_from}`,
      `refresh_to=${refresh_to}`,
    ];
    // First-class page-level filters
    if (filter.geo_level_1) queryParams.push(`geo_level_1=${encodeURIComponent(filter.geo_level_1)}`);
    if (filter.geo_level_2) queryParams.push(`geo_level_2=${encodeURIComponent(filter.geo_level_2)}`);
    if (filter.platform_presence?.length) {
      filter.platform_presence.forEach((p) => queryParams.push(`platform_presence=${encodeURIComponent(p)}`));
    }
    const queryString = `?${queryParams.join('&')}`;
    const url = `${getApiUrl()}/client/${id_client}/country/${country}/franchise/overview/export${queryString}`;

    // Get token for authentication
    const token = await getAuthToken();

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...getApiSecurityHeaders(),
    };

    // Build request body
    const body: Record<string, unknown> = {};
    const tabFilter: Record<string, unknown> = {};
    if (filter.metric_filters && Object.keys(filter.metric_filters).length > 0) {
      tabFilter.metric_filters = filter.metric_filters;
    }
    if (filter.platform_filters && Object.keys(filter.platform_filters).length > 0) {
      tabFilter.platform_filters = filter.platform_filters;
    }
    if (Object.keys(tabFilter).length > 0) body.filter = tabFilter;
    if (sort?.length > 0) body.sort = sort;

    // Make request expecting blob response
    const response = await fetch(url, {
      method: "POST",
      headers: headers,
      body: Object.keys(body).length > 0 ? JSON.stringify(body) : undefined,
    });

    if (!response.ok) {
      throw await buildFranchiseExportError(response);
    }

    // Get the blob from response
    const blob = await response.blob();

    // Get filename from Content-Disposition header if available
    const contentDisposition = response.headers.get("Content-Disposition");
    let filename = `franchise_overview_${id_client}_${country}_${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(
        /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
      );
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, "");
      }
    }

    // Create blob URL and trigger download
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up the blob URL
    window.URL.revokeObjectURL(blobUrl);

    return { success: true, filename };
  } catch (error) {
    // Preserve a shaped API error (carries the backend `detail`/status) so the
    // UI can show it; only wrap genuinely unexpected errors.
    if (error?.status !== undefined) throw error;
    throw new Error("Failed to export franchise overview: " + error.message);
  }
};

/**
 * Export franchise drill-down (outlet) data to CSV
 * First-class page-level filters (geo_level_1, geo_level_2, platform_presence) in query params;
 * tab-specific (metric_filters) and sort in body.
 */
export const exportPhysicalFranchiseDrillDown = async ({
  id_client,
  country,
  franchise_names,
  refresh_from,
  refresh_to,
  filter = {},
  sort = []
}: {
  id_client: string;
  country: string;
  franchise_names: string[];
  refresh_from?: string;
  refresh_to?: string;
  filter?: FranchiseFilterInput;
  sort?: unknown[];
}): Promise<ExportResult> => {
  try {
    const queryParams = [
      `refresh_from=${refresh_from}`,
      `refresh_to=${refresh_to}`,
    ];
    franchise_names.forEach((name) => {
      queryParams.push(`franchise_names=${encodeURIComponent(name)}`);
    });
    if (filter.geo_level_1) queryParams.push(`geo_level_1=${encodeURIComponent(filter.geo_level_1)}`);
    if (filter.geo_level_2) queryParams.push(`geo_level_2=${encodeURIComponent(filter.geo_level_2)}`);
    if (filter.platform_presence?.length) {
      filter.platform_presence.forEach((p) => queryParams.push(`platform_presence=${encodeURIComponent(p)}`));
    }
    const queryString = `?${queryParams.join('&')}`;
    const url = `${getApiUrl()}/client/${id_client}/country/${country}/franchise/outlets/export${queryString}`;

    // Get token for authentication
    const token = await getAuthToken();

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...getApiSecurityHeaders(),
    };

    // Build request body
    const body: Record<string, unknown> = {};
    if (filter.metric_filters && Object.keys(filter.metric_filters).length > 0) {
      body.filter = { metric_filters: filter.metric_filters };
    }
    if (sort?.length > 0) body.sort = sort;

    // Make request expecting blob response
    const response = await fetch(url, {
      method: "POST",
      headers: headers,
      body: Object.keys(body).length > 0 ? JSON.stringify(body) : undefined,
    });

    if (!response.ok) {
      throw await buildFranchiseExportError(response);
    }

    // Get the blob from response
    const blob = await response.blob();

    // Get filename from Content-Disposition header if available
    const contentDisposition = response.headers.get("Content-Disposition");
    let filename = `franchise_outlets_${id_client}_${country}_${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(
        /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
      );
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, "");
      }
    }

    // Create blob URL and trigger download
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up the blob URL
    window.URL.revokeObjectURL(blobUrl);

    return { success: true, filename };
  } catch (error) {
    // Preserve a shaped API error (carries the backend `detail`/status) so the
    // UI can show it; only wrap genuinely unexpected errors.
    if (error?.status !== undefined) throw error;
    throw new Error("Failed to export franchise drill-down: " + error.message);
  }
};

